import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

const DataManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { isAdmin } = useAuth();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("authToken");
      const dataTypes = ["users", "patients", "predictions"];
      const dataType = dataTypes[tabValue];
      const response = await fetch(`/api/data/${dataType}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError("Failed to load data");
      }
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [tabValue]);

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, loadData]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
            Administrator access required to view this page.
          </div>
        </div>
      </div>
    );
  }

  const tabs = ["Users", "Patients", "Predictions"];

  const renderLoading = () => (
    <div className="flex justify-center py-12">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const renderError = () => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
      {error}
    </div>
  );

  const usersColumns = ["ID", "Email", "Role", "Name", "Created", "Last Login"];
  const patientsColumns = ["ID", "Name", "NHS Number", "Created"];
  const predictionsColumns = ["ID", "Patient ID", "Created", "Status"];

  const renderTable = (
    columns: string[],
    rows: any[],
    renderRow: (item: any) => React.ReactNode,
  ) => (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-blue-600 text-white">
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows && rows.length > 0 ? (
            rows.map(renderRow)
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-slate-400 text-sm"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Data Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            View and manage system data (Administrator only)
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200">
          {/* Tabs */}
          <div className="border-b border-slate-200 px-4">
            <div className="flex gap-0">
              {tabs.map((tab, i) => (
                <button
                  key={i}
                  onClick={() => setTabValue(i)}
                  className={`px-5 py-3.5 text-sm font-medium border-b-2 transition-colors ${
                    tabValue === i
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {tabValue === 0 && (
              <div>
                <h2 className="text-base font-semibold text-slate-900 mb-4">
                  Users Data
                </h2>
                {loading
                  ? renderLoading()
                  : error
                    ? renderError()
                    : renderTable(
                        usersColumns,
                        data.users || [],
                        (user: any) => (
                          <tr key={user.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2.5 text-slate-600">
                              {user.id}
                            </td>
                            <td className="px-4 py-2.5 text-slate-800 font-medium">
                              {user.email}
                            </td>
                            <td className="px-4 py-2.5">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.role === "administrator" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}
                              >
                                {user.role}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-slate-600">
                              {user.name || "-"}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {new Date(user.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {user.lastLoginAt
                                ? new Date(
                                    user.lastLoginAt,
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        ),
                      )}
              </div>
            )}

            {tabValue === 1 && (
              <div>
                <h2 className="text-base font-semibold text-slate-900 mb-4">
                  Patients Data
                </h2>
                {loading
                  ? renderLoading()
                  : error
                    ? renderError()
                    : renderTable(
                        patientsColumns,
                        data.patients || [],
                        (patient: any) => (
                          <tr key={patient.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2.5 text-slate-600">
                              {patient.id}
                            </td>
                            <td className="px-4 py-2.5 text-slate-800 font-medium">
                              {patient.name}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600">
                              {patient.nhs}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {new Date(patient.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ),
                      )}
              </div>
            )}

            {tabValue === 2 && (
              <div>
                <h2 className="text-base font-semibold text-slate-900 mb-4">
                  Predictions Data
                </h2>
                {loading
                  ? renderLoading()
                  : error
                    ? renderError()
                    : renderTable(
                        predictionsColumns,
                        data.predictions || [],
                        (prediction: any) => (
                          <tr key={prediction.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2.5 text-slate-600">
                              {prediction.id}
                            </td>
                            <td className="px-4 py-2.5 text-slate-600">
                              {prediction.patientId}
                            </td>
                            <td className="px-4 py-2.5 text-slate-500">
                              {new Date(
                                prediction.createdAt,
                              ).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                {prediction.status || "Completed"}
                              </span>
                            </td>
                          </tr>
                        ),
                      )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagement;
